import { useEffect } from 'react';
import { Alert, Button, Space, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { WizardState, WizardAction } from '../wizardReducer';
import { loadFile } from '../../SmartImport/parsers';
import { buildParsePayload } from '../../SmartImport/buildParsePayload';
import styles from './Step1Upload.module.scss';

const { Dragger } = Upload;

interface Step1UploadProps {
	state: WizardState;
	dispatch: React.Dispatch<WizardAction>;
	onClose: () => void;
	// Pre-supplied file (e.g. attached in the AI chatbot before this modal was
	// opened) — loaded and auto-advanced past this step on mount.
	initialFile?: File | null;
}

const ACCEPTED = '.csv,.xlsx,.xls';

/**
 * Step 1 — Upload. Drag-and-drop / browse, runs header/row detection on the
 * chosen file. Reuses SmartImport's generic file-loading pipeline (`loadFile` +
 * `buildParsePayload`) — header/row detection is not payroll-specific.
 */
export default function Step1Upload({ state, dispatch, onClose, initialFile }: Step1UploadProps) {
	const handleFile = async (file: File, autoAdvance = false) => {
		dispatch({ type: 'FILE_SELECTED', file });

		try {
			const detected = await loadFile(file);
			dispatch({ type: 'PARSE_SUCCEEDED', payload: buildParsePayload(detected) });
			if (autoAdvance) dispatch({ type: 'ADVANCE_STEP' });
		} catch (e) {
			const message = e instanceof Error ? e.message : 'Failed to parse file';
			dispatch({ type: 'PARSE_FAILED', error: message });
		}
	};

	useEffect(() => {
		if (initialFile && !state.file) {
			void handleFile(initialFile, true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialFile]);

	const canContinue =
		!!state.file && state.parseError === null && state.rawMatrix.length > 0;

	return (
		<div className={styles['step1']}>
			<Dragger
				accept={ACCEPTED}
				multiple={false}
				showUploadList={false}
				beforeUpload={(file) => {
					// Return false to prevent antd's default upload; we parse client-side.
					void handleFile(file as unknown as File);
					return false;
				}}
			>
				<p className="ant-upload-drag-icon">
					<InboxOutlined />
				</p>
				<p className="ant-upload-text">Drag an Employee Direct Allocation file here</p>
				<p className="ant-upload-hint">
					or click to browse. Accepts .csv, .xlsx, and .xls files.
				</p>
			</Dragger>

			{state.file && (
				<p className={styles['step1__filename']}>
					Selected file: <strong>{state.file.name}</strong>
				</p>
			)}

			{state.parseError && (
				<Alert
					className={styles['step1__banner']}
					type="error"
					showIcon
					message="Could not read this file"
					description={state.parseError}
				/>
			)}

			<div className={styles['step1__footer']}>
				<Space>
					<Button className={styles['step1__btn-secondary']} onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="primary"
						disabled={!canContinue}
						onClick={() => dispatch({ type: 'ADVANCE_STEP' })}
						className="btn-black"
						style={{ height: '41px' }}
					>
						Continue
					</Button>
				</Space>
			</div>
		</div>
	);
}
